
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AudioAnalyzer, PlaybackState } from './audio/analyzer';
import { AxiomRenderer } from './gl/renderer';
import { EngineParams, AudioFeatures, AxionState } from './types';
import { DEFAULT_PARAMS, PARAM_DESCRIPTIONS, PARAM_CATEGORIES, VERSION, ENGINE_NAME, COPYRIGHT_HTML, PREMADE_ENGINES } from './constants';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

const IconifyIcon = 'iconify-icon' as any;
const MAX_AUDIO_FILE_SIZE = 50 * 1024 * 1024; // 50MB Limit

const AxionLogoMark: React.FC<{ size?: number, color?: string, animated?: boolean }> = ({ size = 24, color = "currentColor", animated = true }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 100 100" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={animated ? "animate-pulse" : ""}
    role="img"
    aria-label="Axion Engine Logo"
    style={{ animationDuration: '4s' }}
  >
    <style>
      {`
        @keyframes drawIn {
          0% { stroke-dashoffset: 400; opacity: 0; }
          50% { stroke-dashoffset: 0; opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 1; }
        }
        .monolith-stroke {
          stroke-dasharray: 400;
          animation: drawIn 3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .monolith-fill {
          opacity: 0;
          animation: fadeIn 2s 1s forwards;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 0.15; }
        }
      `}
    </style>
    <path 
      d="M50 5 L90 27.5 V72.5 L50 95 L10 72.5 V27.5 L50 5Z" 
      stroke={color} 
      strokeWidth="1.5" 
      className="monolith-stroke"
    />
    <path d="M50 5 V95" stroke={color} strokeWidth="0.5" opacity="0.3" />
    <path d="M10 27.5 L50 50 L90 27.5" stroke={color} strokeWidth="1" opacity="0.5" />
    <path d="M10 72.5 L50 50 L90 72.5" stroke={color} strokeWidth="1" className="monolith-stroke" />
    <path d="M50 50 L90 72.5 V27.5 Z" fill={color} className="monolith-fill" />
    <path d="M50 50 L10 72.5 V27.5 Z" fill={color} className="monolith-fill" style={{ animationDelay: '0.5s' }} />
  </svg>
);

const MobileBanner: React.FC = () => (
  <div className="md:hidden fixed top-0 left-0 right-0 z-[100] bg-black/90 border-b border-white/10 px-4 py-2 text-center backdrop-blur-md animate-reveal">
    <p className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">
      Optimal experience via desktop systems
    </p>
  </div>
);

const App: React.FC = () => {
  const { login, logout, isAuthenticated, user } = useKindeAuth();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<AxiomRenderer | null>(null);
  const analyzerRef = useRef<AudioAnalyzer | null>(null);
  
  const [params, setParams] = useState<EngineParams>(DEFAULT_PARAMS);
  const paramsRef = useRef<EngineParams>(DEFAULT_PARAMS);
  const baseParamsRef = useRef<EngineParams>(DEFAULT_PARAMS);
  
  useEffect(() => { paramsRef.current = params; }, [params]);
  const [isActive, setIsActive] = useState(false);
  const [view, setView] = useState<'LANDING' | 'ENGINE' | 'DOCS' | 'ABOUT'>('LANDING');
  const [audioData, setAudioData] = useState<AudioFeatures | null>(null);
  const [playback, setPlayback] = useState<PlaybackState>({ currentTime: 0, duration: 0, isPlaying: false, hasBuffer: false });
  const [isRecording, setIsRecording] = useState(false);
  const [isRecording916, setIsRecording916] = useState(false);
  const [uiView, setUiView] = useState<keyof typeof PARAM_CATEGORIES>('FORM');
  const [hoveredParam, setHoveredParam] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [midiStatus, setMidiStatus] = useState<'IDLE' | 'ACTIVE'>('IDLE');
  const [activeEngineId, setActiveEngineId] = useState<string>("CORE_EVALUATOR");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  const frameIdRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const isRecording916Ref = useRef(false);
  
  const smoothedAudioRef = useRef<AudioFeatures>({
    sub: 0, bass: 0, lowMid: 0, highMid: 0, high: 0, rms: 0, centroid: 0, transient: 0
  });

  useEffect(() => {
    const onMIDIMessage = (event: any) => {
      const [status, data1, data2] = event.data;
      if (status >= 176 && status <= 191) {
        const cc = data1;
        const value = data2 / 127;
        const paramKeys = Object.values(PARAM_CATEGORIES).flat();
        if (cc >= 1 && cc <= paramKeys.length) {
          const key = paramKeys[cc - 1];
          setParams(p => ({ ...p, [key]: value }));
          baseParamsRef.current[key as keyof EngineParams] = value;
        }
      }
    };

    if (navigator.requestMIDIAccess) {
      navigator.requestMIDIAccess().then(
        (access) => {
          setMidiStatus('ACTIVE');
          for (let input of access.inputs.values()) {
            input.onmidimessage = onMIDIMessage;
          }
        },
        () => setMidiStatus('IDLE')
      );
    }
  }, []);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const saveToAxionFile = () => {
    const state: AxionState = {
      format: 'axion-state', version: VERSION, engine: 'Axion',
      timestamp: new Date().toISOString(), params,
      checksum: btoa(JSON.stringify(params)).slice(0, 12)
    };
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `axion_project_${Date.now()}.axion`;
    a.click();
  };

  const loadFromAxionFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const state = JSON.parse(e.target?.result as string) as AxionState;
        if (state.format === 'axion-state') {
          const mergedParams = { ...DEFAULT_PARAMS, ...state.params };
          setParams(mergedParams);
          baseParamsRef.current = { ...mergedParams };
        }
      } catch (err) { console.error('Load failed', err); }
    };
    reader.readAsText(file);
  };

  const exportHighResPNG = (is916: boolean = false) => {
    if (!canvasRef.current || !rendererRef.current) return;
    const canvas = canvasRef.current;
    const renderer = rendererRef.current;
    const originalWidth = canvas.width;
    const originalHeight = canvas.height;
    
    if (is916) {
      canvas.width = 2160;
      canvas.height = 3840;
    } else {
      canvas.width = 3840;
      canvas.height = 2160;
    }
    renderer.resize(canvas.width, canvas.height);
    renderer.render((performance.now() - startTimeRef.current) / 1000, smoothedAudioRef.current, params);
    
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `axion_capture_${is916 ? '9x16' : '4k'}_${Date.now()}.png`;
    a.click();
    
    canvas.width = originalWidth;
    canvas.height = originalHeight;
    renderer.resize(originalWidth, originalHeight);
  };

  const setupAnalyzer = async () => {
    if (!analyzerRef.current) {
      analyzerRef.current = new AudioAnalyzer();
      await analyzerRef.current.setTestMode(true);
      startTimeRef.current = performance.now();
      setIsActive(true);
      setView('ENGINE');
    }
    return analyzerRef.current;
  };

  const handleEnterEngine = async () => {
    if (!isAuthenticated) {
      login();
      return;
    }
    await setupAnalyzer();
  };

  const initMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const analyzer = await setupAnalyzer();
      await analyzer.setStream(stream);
      setErrorMsg(null);
    } catch (err) { 
      console.error('Mic error', err);
      setErrorMsg("MICROPHONE_ACCESS_DENIED");
    }
  };

  const handleFile = async (file: File) => {
    if (file.name.endsWith('.axion')) {
      loadFromAxionFile(file);
      return;
    }
    if (!file.type.startsWith('audio/')) return;
    
    if (file.size > MAX_AUDIO_FILE_SIZE) {
      setErrorMsg("FILE_SIZE_LIMIT_EXCEEDED (50MB MAX)");
      return;
    }

    try {
      const analyzer = await setupAnalyzer();
      const audioBuffer = await analyzer.getAudioContext().decodeAudioData(await file.arrayBuffer());
      await analyzer.setFile(audioBuffer);
      setErrorMsg(null);
    } catch (err) {
      console.error('File load error', err);
      setErrorMsg("SIGNAL_DECODE_FAILURE");
    }
  };

  const [exportProgress, setExportProgress] = useState<number | null>(null);
  const isRecordingRef = useRef(false);
  const lastUiUpdateRef = useRef<number>(0);

  const handleOfflineExport = async (is916: boolean = false) => {
    if (!analyzerRef.current || !rendererRef.current) return;
    const buffer = analyzerRef.current.getBuffer();
    
    // Fallback to real-time recording if using microphone (no buffer)
    if (!buffer) {
      startRecording(is916);
      return;
    }

    try {
      setExportProgress(0);
      const { exportVideoOffline } = await import('./exporter');
      
      const width = is916 ? 1080 : 3840;
      const height = is916 ? 1920 : 2160;
      const fps = 60;
      const bitrate = is916 ? 25000000 : 60000000; // Even higher quality offline

      const blob = await exportVideoOffline(
        buffer,
        rendererRef.current,
        paramsRef.current,
        width,
        height,
        fps,
        bitrate,
        (p) => setExportProgress(p)
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `axion_hq_${is916 ? '9x16' : '4k'}_${Date.now()}.mp4`;
      a.click();
    } catch (err) {
      console.error("Offline export failed:", err);
      setErrorMsg("OFFLINE_EXPORT_FAILED (FALLING BACK TO REALTIME)");
      // Fallback
      startRecording(is916);
    } finally {
      setExportProgress(null);
      handleResize();
    }
  };

  const startRecording = (is916: boolean = false) => {
    if (!canvasRef.current || !analyzerRef.current || !rendererRef.current) return;
    
    const canvas = canvasRef.current;
    
    if (is916) {
      isRecording916Ref.current = true;
      setIsRecording916(true);
      canvas.width = 1080;
      canvas.height = 1920;
      canvas.style.objectFit = 'contain';
      rendererRef.current.resize(1080, 1920);
    }
    
    const canvasStream = canvas.captureStream(60);
    const audioCtx = analyzerRef.current.getAudioContext();
    const dest = audioCtx.createMediaStreamDestination();
    analyzerRef.current.getAnalyzer().connect(dest);
    const mimeType = MediaRecorder.isTypeSupported('video/mp4;codecs=avc1.64002A,mp4a.40.2') 
      ? 'video/mp4;codecs=avc1.64002A,mp4a.40.2' 
      : (MediaRecorder.isTypeSupported('video/mp4;codecs=avc1.42E01F,mp4a.40.2') 
        ? 'video/mp4;codecs=avc1.42E01F,mp4a.40.2' 
        : 'video/webm;codecs=vp9,opus');
    
    const recorder = new MediaRecorder(new MediaStream([...canvasStream.getVideoTracks(), ...dest.stream.getAudioTracks()]), {
      mimeType,
      videoBitsPerSecond: is916 ? 25000000 : 50000000 // Increased bitrate for 4K
    });
    chunksRef.current = [];
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType.split(';')[0] });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `axion_render_${is916 ? '9x16_' : ''}${Date.now()}.${mimeType.includes('mp4') ? 'mp4' : 'webm'}`;
      a.click();
      
      if (isRecording916Ref.current) {
        isRecording916Ref.current = false;
        setIsRecording916(false);
        canvas.style.objectFit = '';
        handleResize();
      }
    };
    recorder.start();
    recorderRef.current = recorder;
    setIsRecording(true);
    isRecordingRef.current = true;
  };

  const stopRecording = () => {
    if (recorderRef.current) {
      recorderRef.current.stop();
      setIsRecording(false);
      isRecordingRef.current = false;
    }
  };

  const handleResize = useCallback(() => {
    if (isRecording916Ref.current) return;
    if (canvasRef.current && rendererRef.current) {
      canvasRef.current.width = window.innerWidth;
      canvasRef.current.height = window.innerHeight;
      rendererRef.current.resize(window.innerWidth, window.innerHeight);
    }
  }, []);

  useEffect(() => {
    if (canvasRef.current && !rendererRef.current) {
      rendererRef.current = new AxiomRenderer(canvasRef.current);
      handleResize();
      window.addEventListener('resize', handleResize);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    const loop = (now: number) => {
      if (isActive && analyzerRef.current && rendererRef.current && view === 'ENGINE') {
        const raw = analyzerRef.current.getFeatures();
        const currentParamsRef = paramsRef.current;
        
        const alpha = 0.5 - (currentParamsRef.inertia * 0.45); 
        smoothedAudioRef.current = {
          sub: smoothedAudioRef.current.sub * (1 - alpha) + raw.sub * alpha,
          bass: smoothedAudioRef.current.bass * (1 - alpha) + raw.bass * alpha,
          lowMid: smoothedAudioRef.current.lowMid * (1 - alpha) + raw.lowMid * alpha,
          highMid: smoothedAudioRef.current.highMid * (1 - alpha) + raw.highMid * alpha,
          high: smoothedAudioRef.current.high * (1 - alpha) + raw.high * alpha,
          rms: smoothedAudioRef.current.rms * (1 - alpha) + raw.rms * alpha,
          centroid: smoothedAudioRef.current.centroid * (1 - alpha) + raw.centroid * alpha,
          transient: raw.transient 
        };
        
        let currentParams = { ...baseParamsRef.current };

        rendererRef.current.render((now - startTimeRef.current) / 1000, smoothedAudioRef.current, currentParams);
        
        const throttleMs = isRecordingRef.current ? 500 : 100;
        if (now - lastUiUpdateRef.current > throttleMs) {
          lastUiUpdateRef.current = now;
          setAudioData({...smoothedAudioRef.current});
          setPlayback(analyzerRef.current.getPlaybackState());
        }
      }
      frameIdRef.current = requestAnimationFrame(loop);
    };
    frameIdRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameIdRef.current);
  }, [isActive, view]);

  return (
    <div 
      className="relative w-screen h-screen overflow-hidden bg-[#010101] text-zinc-400 font-mono"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); const file = e.dataTransfer.files[0]; if (file) handleFile(file); }}
    >
      <MobileBanner />
      <canvas ref={canvasRef} aria-hidden="true" className={`absolute inset-0 z-0 transition-opacity duration-1000 ${view === 'ENGINE' ? 'opacity-100' : 'opacity-20'}`} />

      {/* ERROR OVERLAY */}
      {errorMsg && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[60] glass border-red-500/50 text-red-500 px-6 py-3 text-[10px] font-bold uppercase tracking-widest flex items-center gap-4 animate-reveal">
           <IconifyIcon icon="lucide:alert-triangle" className="text-lg"></IconifyIcon>
           {errorMsg}
           <button onClick={() => setErrorMsg(null)} className="ml-4 opacity-40 hover:opacity-100">DISMISS</button>
        </div>
      )}

      {/* EXPORT PROGRESS OVERLAY */}
      {exportProgress !== null && (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex flex-col items-center justify-center animate-in fade-in duration-500">
          <div className="w-full max-w-md space-y-8 p-8 border border-white/10 bg-white/[0.02]">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-white">
              <span className="flex items-center gap-3">
                <IconifyIcon icon="lucide:loader-2" className="animate-spin text-lg opacity-50" />
                {exportProgress < 0.1 ? 'ANALYZING_AUDIO' : 'ENCODING_VIDEO'}
              </span>
              <span>{Math.round(exportProgress * 100)}%</span>
            </div>
            
            <div className="h-1 w-full bg-white/10 overflow-hidden relative">
              <div 
                className="absolute inset-y-0 left-0 bg-white transition-all duration-300 ease-out"
                style={{ width: `${exportProgress * 100}%` }}
              />
            </div>
            
            <div className="text-[9px] text-white/40 uppercase tracking-[0.3em] text-center leading-relaxed">
              Synthesizing high-fidelity deterministic frames.<br/>
              Please do not close this window.
            </div>
          </div>
        </div>
      )}

      {/* LANDING VIEW */}
      {view === 'LANDING' && (
        <main className="absolute inset-0 z-20 overflow-y-auto bg-black/80 backdrop-blur-lg px-6 md:px-8 py-12 md:py-20 animate-reveal">
          <div className="max-w-4xl mx-auto space-y-16 md:space-y-32">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8 border-b border-white/5 pb-12 opacity-0 animate-reveal stagger-1">
              <div className="space-y-4 flex items-end gap-8">
                <AxionLogoMark size={64} color="white" />
                <div className="space-y-1">
                  <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tighter leading-none uppercase">AXION</h1>
                  <p className="text-[10px] md:text-xs opacity-40 uppercase tracking-[0.6em]">Deterministic Signal Engine</p>
                </div>
              </div>
              <div className="flex gap-4">
                <a href="https://status.rishishah.in" target="_blank" className="flex items-center gap-3 px-6 py-2 border border-white/10 rounded-sm group hover:bg-white/5 transition-all">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-white/60 group-hover:text-white">SYSTEM_OPERATIONAL</span>
                </a>
              </div>
            </header>
            
            <section className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-32 opacity-0 animate-reveal stagger-2">
              <div className="space-y-8">
                <h2 className="text-white text-[12px] font-bold tracking-[0.4em] uppercase border-l-2 border-white/20 pl-4 mb-6">Why this exists</h2>
                <div className="space-y-8 opacity-60 text-[10px] md:text-[11px] leading-relaxed uppercase tracking-wider text-justify">
                  <p>
                    Axion was built as an internal tool to explore how sound behaves when treated as a continuous signal inside mathematical systems.
                  </p>
                  <p>
                    It stayed internal because it was opinionated and unforgiving.
                  </p>
                  <p>
                    I’m making it public because it’s stable enough now, and because tools like this don’t benefit from being hidden.
                  </p>
                  <p>
                    Nothing about it was simplified for release.
                  </p>
                </div>
              </div>
              <div className="space-y-6 hidden md:block">
                <h2 className="text-white text-[10px] font-bold tracking-[0.4em] uppercase border-l-2 border-white/20 pl-4">AXION_SPEC</h2>
                <div className="space-y-4 opacity-40 text-[10px] leading-relaxed uppercase tracking-widest">
                  <div className="flex justify-between border-b border-white/5 pb-1"><span>FILE_LIMIT</span><span>50_MB</span></div>
                  <div className="flex justify-between border-b border-white/5 pb-1"><span>REACTIVITY</span><span>SPECTRAL_SYNC</span></div>
                  <div className="flex justify-between border-b border-white/5 pb-1"><span>FFT_RES</span><span>2048_BINS</span></div>
                </div>
              </div>
            </section>

            <nav className="flex flex-col md:flex-row gap-6 md:gap-8 py-12 border-t border-white/5 opacity-0 animate-reveal stagger-3">
              <button 
                onClick={handleEnterEngine}
                className="flex-1 px-8 py-6 md:py-7 bg-white text-black text-[11px] font-bold tracking-[0.4em] uppercase hover:bg-zinc-200 active:scale-[0.98] transition-all flex items-center justify-between group focus:outline-none focus:ring-2 focus:ring-white"
              >
                ENTER_ENGINE
                <IconifyIcon icon="lucide:terminal" className="text-2xl opacity-20 group-hover:opacity-100 transition-all"></IconifyIcon>
              </button>
              <button 
                onClick={() => setView('DOCS')}
                className="flex-1 px-8 py-6 md:py-7 border border-white/20 text-white text-[11px] font-bold tracking-[0.4em] uppercase hover:bg-white/10 active:scale-[0.98] transition-all flex items-center justify-between group focus:outline-none focus:ring-2 focus:ring-white"
              >
                VIEW_SPECIFICATION
                <IconifyIcon icon="lucide:file-code" className="text-2xl opacity-20 group-hover:opacity-100 transition-all"></IconifyIcon>
              </button>
              <button 
                onClick={() => setView('ABOUT')}
                className="flex-1 px-8 py-6 md:py-7 border border-white/20 text-white text-[11px] font-bold tracking-[0.4em] uppercase hover:bg-white/10 active:scale-[0.98] transition-all flex items-center justify-between group focus:outline-none focus:ring-2 focus:ring-white"
              >
                ABOUT_AXION
                <IconifyIcon icon="lucide:info" className="text-2xl opacity-20 group-hover:opacity-100 transition-all"></IconifyIcon>
              </button>
            </nav>
            <footer className="opacity-20 text-[9px] md:text-[10px] uppercase text-center pb-20 tracking-[0.3em]">
              <span dangerouslySetInnerHTML={{ __html: COPYRIGHT_HTML }} />
            </footer>
          </div>
        </main>
      )}

      {/* DOCS VIEW */}
      {view === 'DOCS' && (
        <main className="absolute inset-0 z-40 bg-black/98 overflow-y-auto px-6 md:px-12 py-12 md:py-24 animate-reveal text-zinc-400 selection:bg-white selection:text-black">
           <button 
            onClick={() => setView(isActive ? 'ENGINE' : 'LANDING')} 
            className="fixed top-8 left-6 md:top-12 md:left-12 text-white border border-white/10 px-6 py-2 hover:bg-white/5 transition-all z-50 text-[10px] uppercase tracking-[0.4em] flex items-center gap-3 group backdrop-blur-xl"
           >
            <IconifyIcon icon="lucide:arrow-left" className="group-hover:-translate-x-1 transition-transform"></IconifyIcon>
            EXIT_DOCS
           </button>

           <article className="max-w-5xl mx-auto space-y-40 pb-64">
              <header className="space-y-10">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-10 border-b border-white/10 pb-20">
                   <AxionLogoMark size={120} color="white" animated={false} />
                   <div className="space-y-4">
                      <h2 className="text-5xl md:text-8xl text-white tracking-tighter uppercase font-bold leading-none">TECHNICAL_SPEC</h2>
                      <div className="flex gap-6 items-center opacity-40">
                        <span className="text-xs uppercase tracking-[0.5em] font-bold">V{VERSION}</span>
                        <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                        <span className="text-xs uppercase tracking-[0.5em] font-bold">RELEASE_CANDIDATE_01</span>
                      </div>
                   </div>
                </div>
              </header>

              <section className="space-y-16 animate-reveal stagger-1">
                 <div className="flex items-end gap-6 border-b border-white/5 pb-6">
                    <span className="text-4xl text-white/10 font-bold tabular-nums">01</span>
                    <h3 className="text-2xl text-white font-bold uppercase tracking-[0.3em]">SYSTEM_ARCHITECTURE</h3>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-justify">
                    <div className="md:col-span-2 space-y-8 text-[11px] leading-relaxed uppercase tracking-widest opacity-70">
                       <p>Axion is a real-time volumetric evaluator built on a WebGL2 raymarching pipeline. Geometry is not rasterized from triangles but sphere-traced from Signed Distance Functions (SDFs). This ensures topological precision and mathematically perfect blending between surfaces.</p>
                       <p>The core evaluator performs up to 90 steps per pixel per frame, utilizing a fixed epsilon of 0.0008 to define manifold boundaries. Surface normals are calculated via central differences, allowing for signal-driven lighting modulation.</p>
                    </div>
                    <div className="bg-white/[0.02] p-8 border border-white/10 space-y-6">
                       <h4 className="text-[10px] font-bold text-white/40 tracking-widest uppercase">RENDER_STATE</h4>
                       <div className="space-y-3 font-mono text-[9px] tracking-widest uppercase">
                          <div className="flex justify-between border-b border-white/5 pb-1"><span>Target</span><span className="text-white">60FPS_LOCKED</span></div>
                          <div className="flex justify-between border-b border-white/5 pb-1"><span>Buffer</span><span className="text-white">RGBA8_LINEAR</span></div>
                          <div className="flex justify-between border-b border-white/5 pb-1"><span>Intersection</span><span className="text-white">SPHERE_TRACE</span></div>
                          <div className="flex justify-between border-b border-white/5 pb-1"><span>Epsilon</span><span className="text-white">0.0008</span></div>
                       </div>
                    </div>
                 </div>
              </section>

              <section className="space-y-16 animate-reveal stagger-2">
                 <div className="flex items-end gap-6 border-b border-white/5 pb-6">
                    <span className="text-4xl text-white/10 font-bold tabular-nums">02</span>
                    <h3 className="text-2xl text-white font-bold uppercase tracking-[0.3em]">SIGNAL_ACQUISITION</h3>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-justify">
                    <div className="md:col-span-2 space-y-8 text-[11px] leading-relaxed uppercase tracking-widest opacity-70">
                       <p>Signal processing uses a 2048-sample FFT. Analysis occurs across five discrete frequency bands: Sub-Bass (20-60Hz), Bass (60-250Hz), Low-Mid (250-1kHz), High-Mid (1k-4kHz), and High (4k-12kHz). Derived features include RMS loudness and Spectral Centroid.</p>
                       <p>Audio reactivity is strictly enforced. If the incoming RMS level falls below 0.001, the engine halts all signal-induced coordinate warping and displacement to maintain deterministic stability.</p>
                    </div>
                    <div className="bg-white/[0.02] p-8 border border-white/10 space-y-6">
                       <h4 className="text-[10px] font-bold text-white/40 tracking-widest uppercase">INPUT_LIMITS</h4>
                       <div className="space-y-3 font-mono text-[9px] tracking-widest uppercase">
                          <div className="flex justify-between border-b border-white/5 pb-1"><span>FFT_Size</span><span className="text-white">2048</span></div>
                          <div className="flex justify-between border-b border-white/5 pb-1"><span>File_Max</span><span className="text-white">50_MB</span></div>
                          <div className="flex justify-between border-b border-white/5 pb-1"><span>Bitrate</span><span className="text-white">VARIABLE</span></div>
                          <div className="flex justify-between border-b border-white/5 pb-1"><span>Channels</span><span className="text-white">MONO_SUM</span></div>
                       </div>
                    </div>
                 </div>
              </section>

              <footer className="pt-20 border-t border-white/10 text-center space-y-8">
                 <p className="text-[9px] uppercase tracking-[0.8em] font-bold opacity-30">END_OF_SPECIFICATION</p>
                 <div className="flex justify-center gap-12 opacity-20 text-[10px] uppercase font-bold tracking-[0.4em]">
                    <span>STATUS: {isActive ? 'ACTIVE' : 'IDLE'}</span>
                    <span>SIGNAL: {audioData && audioData.rms > 0.01 ? 'TRANSMITTING' : 'SILENCE'}</span>
                 </div>
              </footer>
           </article>
        </main>
      )}

      {/* ABOUT VIEW */}
      {view === 'ABOUT' && (
        <main className="absolute inset-0 z-40 bg-black/98 overflow-y-auto px-6 md:px-12 py-12 md:py-24 animate-reveal text-zinc-400 selection:bg-white selection:text-black">
           <button 
            onClick={() => setView('LANDING')} 
            className="fixed top-8 left-6 md:top-12 md:left-12 text-white border border-white/10 px-6 py-2 hover:bg-white/5 transition-all z-50 text-[10px] uppercase tracking-[0.4em] flex items-center gap-3 group backdrop-blur-xl"
           >
            <IconifyIcon icon="lucide:arrow-left" className="group-hover:-translate-x-1 transition-transform"></IconifyIcon>
            RETURN_TO_BASE
           </button>

           <article className="max-w-4xl mx-auto space-y-32 pb-64">
              <header className="space-y-10 border-b border-white/10 pb-20">
                 <div className="space-y-4">
                    <h2 className="text-4xl md:text-7xl text-white tracking-tighter uppercase font-bold leading-none">SYSTEM_CAPABILITIES</h2>
                    <p className="text-xs md:text-sm uppercase tracking-[0.5em] font-bold opacity-40">Core operational parameters and engine features.</p>
                 </div>
              </header>

              <section className="space-y-12 animate-reveal stagger-1">
                 <div className="flex items-end gap-6 border-b border-white/5 pb-6">
                    <span className="text-4xl text-white/10 font-bold tabular-nums">01</span>
                    <h3 className="text-2xl text-white font-bold uppercase tracking-[0.3em]">FORM_GENERATION</h3>
                 </div>
                 <ul className="space-y-6 text-[11px] leading-relaxed uppercase tracking-widest opacity-80 list-none pl-0">
                    <li className="flex gap-4"><IconifyIcon icon="lucide:box" className="text-white mt-0.5"></IconifyIcon><span><strong>Independent Axis Topology:</strong> Interpolate between primitives (Sphere, Box, Torus, Octahedron, Cylinder) independently across X, Y, and Z axes.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:maximize" className="text-white mt-0.5"></IconifyIcon><span><strong>Volumetric Scaling:</strong> Isotropic magnitude control within the viewing frustum, directly coupled to audio RMS.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:grid" className="text-white mt-0.5"></IconifyIcon><span><strong>Lattice Repetition:</strong> Infinite domain repetition utilizing modular coordinate arithmetic.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:split-square-horizontal" className="text-white mt-0.5"></IconifyIcon><span><strong>Spatial Symmetry:</strong> Orthogonal Cartesian reflection and rotative spatial mirroring.</span></li>
                 </ul>
              </section>

              <section className="space-y-12 animate-reveal stagger-2">
                 <div className="flex items-end gap-6 border-b border-white/5 pb-6">
                    <span className="text-4xl text-white/10 font-bold tabular-nums">02</span>
                    <h3 className="text-2xl text-white font-bold uppercase tracking-[0.3em]">ENERGY_MODULATION</h3>
                 </div>
                 <ul className="space-y-6 text-[11px] leading-relaxed uppercase tracking-widest opacity-80 list-none pl-0">
                    <li className="flex gap-4"><IconifyIcon icon="lucide:activity" className="text-white mt-0.5"></IconifyIcon><span><strong>Audio Drive:</strong> Direct coupling coefficient between signal RMS and geometric displacement.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:clock" className="text-white mt-0.5"></IconifyIcon><span><strong>Temporal Inertia:</strong> Exponential moving average applied to signal features for physical momentum simulation.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:zap" className="text-white mt-0.5"></IconifyIcon><span><strong>Transient Impact:</strong> Sensitivity to the first derivative of the spectral energy density, driving sudden structural shifts.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:rotate-cw" className="text-white mt-0.5"></IconifyIcon><span><strong>Phase Oscillation:</strong> Deterministic angular velocity of the global phase accumulator.</span></li>
                 </ul>
              </section>

              <section className="space-y-12 animate-reveal stagger-3">
                 <div className="flex items-end gap-6 border-b border-white/5 pb-6">
                    <span className="text-4xl text-white/10 font-bold tabular-nums">03</span>
                    <h3 className="text-2xl text-white font-bold uppercase tracking-[0.3em]">BEHAVIORAL_OPERATORS</h3>
                 </div>
                 <ul className="space-y-6 text-[11px] leading-relaxed uppercase tracking-widest opacity-80 list-none pl-0">
                    <li className="flex gap-4"><IconifyIcon icon="lucide:waves" className="text-white mt-0.5"></IconifyIcon><span><strong>Domain Warping:</strong> High-frequency trigonometric interference deforming the coordinate space.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:fold-horizontal" className="text-white mt-0.5"></IconifyIcon><span><strong>Recursive Folding:</strong> Iterative absolute-value folding of the coordinate manifold.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:hash" className="text-white mt-0.5"></IconifyIcon><span><strong>Spatial Quantization:</strong> Discretization of the spatial grid to finite bit-depth resolutions.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:radio" className="text-white mt-0.5"></IconifyIcon><span><strong>Harmonic Resonance:</strong> Spatial feedback loop scale within the evaluator.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:scatter-chart" className="text-white mt-0.5"></IconifyIcon><span><strong>Stochastic Scatter:</strong> Noise-driven distribution of spatial coordinates.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:network" className="text-white mt-0.5"></IconifyIcon><span><strong>Fractal Iteration:</strong> Recursive fractal iteration intensity driven by trigonometric functions.</span></li>
                 </ul>
              </section>
              
              <section className="space-y-12 animate-reveal stagger-4">
                 <div className="flex items-end gap-6 border-b border-white/5 pb-6">
                    <span className="text-4xl text-white/10 font-bold tabular-nums">04</span>
                    <h3 className="text-2xl text-white font-bold uppercase tracking-[0.3em]">SYSTEM_EVALUATION</h3>
                 </div>
                 <ul className="space-y-6 text-[11px] leading-relaxed uppercase tracking-widest opacity-80 list-none pl-0">
                    <li className="flex gap-4"><IconifyIcon icon="lucide:cloud-drizzle" className="text-white mt-0.5"></IconifyIcon><span><strong>Dither Grain:</strong> Deterministic hash-based dither density for spatial entropy simulation.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:palette" className="text-white mt-0.5"></IconifyIcon><span><strong>Spectral Chroma:</strong> Tri-color phase-shift mapping during lighting evaluation, driven by spectral centroid.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:layers" className="text-white mt-0.5"></IconifyIcon><span><strong>Atmospheric Depth:</strong> Exponential atmospheric attenuation based on ray-traversal distance.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:video" className="text-white mt-0.5"></IconifyIcon><span><strong>Export Capabilities:</strong> Real-time WebM video capture and high-resolution (4K & 9:16) PNG snapshots.</span></li>
                    <li className="flex gap-4"><IconifyIcon icon="lucide:save" className="text-white mt-0.5"></IconifyIcon><span><strong>State Persistence:</strong> Export and import engine parameters as .axion project files.</span></li>
                 </ul>
              </section>

              <footer className="pt-20 border-t border-white/10 text-center space-y-8">
                 <p className="text-[9px] uppercase tracking-[0.8em] font-bold opacity-30">END_OF_TRANSMISSION</p>
              </footer>
           </article>
        </main>
      )}

      {/* ENGINE HUD */}
      {view === 'ENGINE' && (
        <div className="h-full flex flex-col">
          <header className="fixed top-10 left-10 z-30 flex items-center gap-6 pointer-events-auto">
            <AxionLogoMark size={32} color="white" />
            <div>
              <h1 className="text-sm md:text-lg font-bold text-white tracking-[0.4em] uppercase">{ENGINE_NAME} // v{VERSION}</h1>
              <div className="flex gap-4 items-center opacity-30 text-[9px] uppercase tracking-widest mt-1">
                <span className="flex items-center gap-1.5 font-bold text-green-400">
                    <IconifyIcon icon="lucide:user-check"></IconifyIcon> {user?.given_name || 'IDENT_VERIFIED'}
                </span>
              </div>
            </div>
          </header>

          <aside className="fixed top-36 left-10 z-20 hidden md:flex flex-col gap-12 pr-4">
            <nav className="space-y-5">
              <p className="opacity-20 text-[9px] border-b border-white/5 pb-2 uppercase tracking-[0.3em] font-bold">Input_Engine</p>
              <div className="flex flex-col gap-4">
                <button onClick={initMic} className="text-left hover:text-white transition-all flex items-center gap-3 text-[11px] group uppercase tracking-widest">
                  <IconifyIcon icon="lucide:mic" className="opacity-20 group-hover:opacity-100"></IconifyIcon> LIVE_MIC
                </button>
                <label className="text-left hover:text-white transition-all cursor-pointer flex items-center gap-3 text-[11px] group uppercase tracking-widest">
                  <IconifyIcon icon="lucide:music" className="opacity-20 group-hover:opacity-100"></IconifyIcon> LOAD_TRACK
                  <input type="file" accept="audio/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
                </label>
              </div>
            </nav>

            <nav className="space-y-5">
              <p className="opacity-20 text-[9px] border-b border-white/5 pb-2 uppercase tracking-[0.3em] font-bold">Persistence</p>
              <div className="flex flex-col gap-4">
                <button onClick={saveToAxionFile} className="text-left hover:text-white transition-all flex items-center gap-3 text-[11px] group uppercase tracking-widest">
                  <IconifyIcon icon="lucide:upload" className="opacity-20 group-hover:opacity-100"></IconifyIcon> EXPORT_.AXION
                </button>
                <label className="text-left hover:text-white transition-all cursor-pointer flex items-center gap-3 text-[11px] group uppercase tracking-widest">
                  <IconifyIcon icon="lucide:download" className="opacity-20 group-hover:opacity-100"></IconifyIcon> IMPORT_.AXION
                  <input type="file" accept=".axion" className="hidden" onChange={(e) => {
                    if (e.target.files?.[0]) {
                      loadFromAxionFile(e.target.files[0]);
                      e.target.value = '';
                    }
                  }} />
                </label>
                <button onClick={() => exportHighResPNG(false)} className="text-left hover:text-white transition-all flex items-center gap-3 text-[11px] group uppercase tracking-widest">
                  <IconifyIcon icon="lucide:camera" className="opacity-20 group-hover:opacity-100"></IconifyIcon> 4K_SNAPSHOT
                </button>
                <button onClick={() => exportHighResPNG(true)} className="text-left hover:text-white transition-all flex items-center gap-3 text-[11px] group uppercase tracking-widest">
                  <IconifyIcon icon="lucide:smartphone" className="opacity-20 group-hover:opacity-100"></IconifyIcon> 9:16_SNAPSHOT
                </button>
              </div>
            </nav>

            <button onClick={() => logout()} className="text-left text-red-500/60 hover:text-red-500 transition-all flex items-center gap-3 text-[11px] group uppercase tracking-widest mt-4">
              <IconifyIcon icon="lucide:log-out" className="opacity-20 group-hover:opacity-100"></IconifyIcon> SIGN_OUT
            </button>
          </aside>

          <main className={`fixed bottom-10 right-10 z-30 transition-all duration-700 ease-[cubic-bezier(0.2,0,0.2,1)] ${isMinimized ? 'w-[320px]' : 'w-[480px]'}`}>
            <section className="glass shadow-[0_32px_120px_rgba(0,0,0,0.8)] border border-white/10 rounded-sm overflow-hidden flex flex-col">
              
              <div className="p-4 border-b border-white/5 bg-white/[0.02] flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col">
                    <span className="text-[8px] font-bold opacity-20 uppercase tracking-[0.3em]">Signal_Phase</span>
                    <div className="flex gap-1 mt-1">
                      {[0, 1, 2, 3].map(i => (
                        <div key={i} className={`h-3 w-1.5 transition-all duration-300 ${audioData && audioData.rms * 10 > i ? 'bg-white' : 'bg-white/5'}`} />
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[8px] font-bold opacity-20 uppercase tracking-[0.3em]">Signal_Level</span>
                    <span className={`text-[10px] font-bold tracking-widest ${audioData && audioData.rms > 0.001 ? 'text-green-400' : 'text-white'}`}>
                      {audioData ? (audioData.rms * 100).toFixed(1) : '00.0'}%
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => setIsMinimized(!isMinimized)}
                    className="w-8 h-8 flex items-center justify-center hover:bg-white/5 transition-all text-white/40 hover:text-white"
                    title={isMinimized ? "Maximize Console" : "Minimize Console"}
                  >
                    <IconifyIcon icon={isMinimized ? "lucide:maximize-2" : "lucide:minimize-2"} className="text-lg"></IconifyIcon>
                  </button>
                </div>
              </div>

              {playback.hasBuffer && !isMinimized && (
                <div className="px-8 pt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={() => playback.isPlaying ? analyzerRef.current?.pause() : analyzerRef.current?.play()}
                        className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-white transition-all text-white group"
                      >
                        <IconifyIcon icon={playback.isPlaying ? "lucide:pause" : "lucide:play"} className="text-xl group-hover:scale-110 transition-transform"></IconifyIcon>
                      </button>
                      <div className="flex flex-col">
                        <span className="text-[8px] font-bold opacity-20 uppercase tracking-[0.3em]">Transport</span>
                        <span className="text-[10px] font-bold text-white tracking-widest uppercase">{playback.isPlaying ? 'Active' : 'Halted'}</span>
                      </div>
                    </div>
                    <div className="text-right flex flex-col">
                      <span className="text-[8px] font-bold opacity-20 uppercase tracking-[0.3em]">Temporal_Loc</span>
                      <span className="text-[10px] font-bold text-white tracking-widest">
                        {formatTime(playback.currentTime)} / {formatTime(playback.duration)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="group relative pt-2">
                    <input 
                      type="range" min="0" max={playback.duration} step="0.1"
                      value={playback.currentTime}
                      onChange={(e) => analyzerRef.current?.seek(parseFloat(e.target.value))}
                      className="w-full h-1 bg-white/5 appearance-none cursor-pointer accent-white z-10 relative"
                    />
                  </div>
                </div>
              )}

              <div className={`transition-all duration-500 overflow-hidden flex flex-col ${isMinimized ? 'max-h-0 opacity-0' : 'max-h-[80vh] opacity-100'}`}>
                <div className="p-8 space-y-8 overflow-y-auto flex-1">
                  <div className="space-y-4">
                    <p className="opacity-20 text-[9px] uppercase tracking-[0.4em] font-bold border-l-2 border-white/20 pl-3">Architecture_Select</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.keys(PREMADE_ENGINES).map(id => (
                        <button 
                          key={id} 
                          onClick={() => { 
                            setParams(PREMADE_ENGINES[id]); 
                            baseParamsRef.current = { ...PREMADE_ENGINES[id] };
                            setActiveEngineId(id); 
                          }}
                          className={`text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest border transition-all ${activeEngineId === id ? 'bg-white text-black border-white' : 'border-white/5 opacity-40 hover:opacity-100 hover:bg-white/5'}`}
                        >
                          {id.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  <nav className="flex gap-6 border-b border-white/5 pb-2">
                    {(Object.keys(PARAM_CATEGORIES) as Array<keyof typeof PARAM_CATEGORIES>).map(cat => (
                      <button 
                        key={cat} onClick={() => setUiView(cat)}
                        className={`text-[10px] transition-all tracking-[0.2em] font-bold uppercase pb-2 relative ${uiView === cat ? 'text-white' : 'opacity-20 hover:opacity-60'}`}
                      >
                        {cat}
                        {uiView === cat && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />}
                      </button>
                    ))}
                  </nav>

                  <div className="grid grid-cols-2 gap-x-10 gap-y-8">
                    {PARAM_CATEGORIES[uiView].map((key) => (
                      <div key={key} className="space-y-3 group" onMouseEnter={() => setHoveredParam(key)} onMouseLeave={() => setHoveredParam(null)}>
                        <div className="flex justify-between items-center text-[9px] tracking-widest font-bold">
                          <label htmlFor={`param-${key}`} className="opacity-30 group-hover:opacity-100 transition-all uppercase">{key}</label>
                          <output className="text-white opacity-40 group-hover:opacity-100">{(params as any)[key]?.toFixed(3)}</output>
                        </div>
                        <div className="relative h-2 flex items-center">
                          <input 
                            id={`param-${key}`} type="range" min="0" max="1" step="0.001"
                            value={(params as any)[key] || 0}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              setParams(p => ({...p, [key]: val}));
                              baseParamsRef.current[key as keyof EngineParams] = val;
                              setActiveEngineId("CUSTOM");
                            }}
                            className="w-full h-0.5 bg-white/10 appearance-none cursor-pointer accent-white hover:accent-zinc-400 transition-all z-10"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <footer className="p-8 pt-0 space-y-8 shrink-0">
                  <div className="text-[10px] opacity-20 lowercase h-8 flex items-end leading-relaxed font-bold tracking-wider italic">
                    {hoveredParam ? PARAM_DESCRIPTIONS[hoveredParam] : "// signal-driven volumetric evaluator"}
                  </div>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => { 
                        setParams(DEFAULT_PARAMS); 
                        baseParamsRef.current = { ...DEFAULT_PARAMS };
                        setActiveEngineId("CORE_EVALUATOR"); 
                      }} 
                      className="px-6 py-4 border border-white/10 hover:border-white text-[10px] font-bold uppercase tracking-widest transition-all"
                    >
                      CLR
                    </button>
                    <button 
                      onClick={() => isRecording ? stopRecording() : startRecording(false)}
                      className={`flex-1 px-4 py-4 border transition-all text-[10px] font-bold uppercase tracking-widest ${isRecording && !isRecording916 ? 'border-red-500 text-red-500 bg-red-500/10' : 'border-white/10 hover:bg-white hover:text-black'} ${isRecording && isRecording916 ? 'opacity-20 cursor-not-allowed' : ''}`}
                    >
                      {isRecording && !isRecording916 ? 'HALT_RECORD' : 'LIVE_RECORD'}
                    </button>
                    <button 
                      onClick={() => isRecording ? stopRecording() : startRecording(true)}
                      className={`flex-1 px-4 py-4 border transition-all text-[10px] font-bold uppercase tracking-widest ${isRecording && isRecording916 ? 'border-red-500 text-red-500 bg-red-500/10' : 'border-white/10 hover:bg-white hover:text-black'} ${isRecording && !isRecording916 ? 'opacity-20 cursor-not-allowed' : ''}`}
                    >
                      {isRecording && isRecording916 ? 'HALT_9:16' : 'LIVE_9:16'}
                    </button>
                  </div>
                </footer>
              </div>

              {isMinimized && (
                <div className="p-4 bg-white/[0.01] flex justify-between items-center animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="flex gap-3">
                    <button onClick={initMic} className="text-[9px] font-bold opacity-30 hover:opacity-100 uppercase tracking-widest">Mic</button>
                    <button onClick={saveToAxionFile} className="text-[9px] font-bold opacity-30 hover:opacity-100 uppercase tracking-widest">Save</button>
                  </div>
                  {playback.hasBuffer && (
                    <div className="flex items-center gap-3">
                       <span className="text-[8px] opacity-20 font-bold uppercase tabular-nums">{formatTime(playback.currentTime)}</span>
                       <button onClick={() => playback.isPlaying ? analyzerRef.current?.pause() : analyzerRef.current?.play()} className="text-white">
                          <IconifyIcon icon={playback.isPlaying ? "lucide:pause" : "lucide:play"}></IconifyIcon>
                       </button>
                    </div>
                  )}
                  {!playback.hasBuffer && <span className="text-[8px] opacity-10 font-bold uppercase tracking-[0.5em]">Axion_Idle</span>}
                </div>
              )}
            </section>
          </main>

          <footer className="fixed bottom-6 left-10 right-10 z-0 hidden md:flex justify-between items-end opacity-20 font-mono text-[9px] uppercase tracking-[0.4em] pointer-events-none">
            <div dangerouslySetInnerHTML={{ __html: COPYRIGHT_HTML }} className="pointer-events-auto" />
            <div className="text-right">60FPS_LOCKED // BUF_SYNC_OK</div>
          </footer>
        </div>
      )}
    </div>
  );
};

export default App;
